import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Calendar,
  Clock,
  MapPin,
  Phone,
  Mail,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw,
  Filter,
} from 'lucide-react';
import bookingsApi, { type Booking, type BookingStatus, type StatusHistoryEntry } from '@/api/bookings';
import { toast } from 'sonner';

export const Route = createFileRoute('/dashboard/appointments')({
  component: PetsoAppointments,
});

// ─── Status config ──────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<BookingStatus, { label: string; color: string; bgColor: string; icon: string }> = {
  new: { label: 'New', color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-50 dark:bg-blue-900/20', icon: '📋' },
  pending_assignment: { label: 'Pending Assignment', color: 'text-yellow-600 dark:text-yellow-400', bgColor: 'bg-yellow-50 dark:bg-yellow-900/20', icon: '⏳' },
  assigned: { label: 'Assigned', color: 'text-indigo-600 dark:text-indigo-400', bgColor: 'bg-indigo-50 dark:bg-indigo-900/20', icon: '👤' },
  confirmed: { label: 'Confirmed', color: 'text-emerald-600 dark:text-emerald-400', bgColor: 'bg-emerald-50 dark:bg-emerald-900/20', icon: '✅' },
  in_progress: { label: 'In Progress', color: 'text-purple-600 dark:text-purple-400', bgColor: 'bg-purple-50 dark:bg-purple-900/20', icon: '🔄' },
  completed: { label: 'Completed', color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-50 dark:bg-green-900/20', icon: '🎉' },
  cancelled: { label: 'Cancelled', color: 'text-red-600 dark:text-red-400', bgColor: 'bg-red-50 dark:bg-red-900/20', icon: '❌' },
  no_show: { label: 'No Show', color: 'text-orange-600 dark:text-orange-400', bgColor: 'bg-orange-50 dark:bg-orange-900/20', icon: '⚠️' },
  rescheduled: { label: 'Rescheduled', color: 'text-cyan-600 dark:text-cyan-400', bgColor: 'bg-cyan-50 dark:bg-cyan-900/20', icon: '🔄' },
};

// ─── Status timeline steps ──────────────────────────────────────────────────

const ACTIVE_FLOW_STEPS: BookingStatus[] = ['new', 'assigned', 'confirmed', 'in_progress', 'completed'];

function getStepIndex(status: BookingStatus): number {
  const idx = ACTIVE_FLOW_STEPS.indexOf(status);
  return idx >= 0 ? idx : -1;
}

// ─── Filter Tabs ────────────────────────────────────────────────────────────

type FilterTab = 'upcoming' | 'active' | 'past' | 'all';

// ─── Component ──────────────────────────────────────────────────────────────

function PetsoAppointments() {
  const { t } = useTranslation();
  const [bookingsList, setBookingsList] = useState<Booking[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [statusHistory, setStatusHistory] = useState<Record<string, StatusHistoryEntry[]>>({});
  const [activeFilter, setActiveFilter] = useState<FilterTab>('upcoming');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const fetchBookings = useCallback(async () => {
    try {
      setIsLoading(true);
      const statusFilter = activeFilter === 'active'
        ? undefined // We'll filter client-side for active
        : activeFilter === 'past'
          ? undefined // Filter client-side for past
          : undefined;

      const res = await bookingsApi.list({ page, limit: 20 });
      setBookingsList(res.bookings);
      setTotalPages(res.pagination.totalPages);
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
      toast.error(t('appointments_fetch_error', 'Failed to load appointments'));
    } finally {
      setIsLoading(false);
    }
  }, [page, activeFilter, t]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  // Auto-refresh every 30 seconds for live tracking
  useEffect(() => {
    const interval = setInterval(fetchBookings, 30000);
    return () => clearInterval(interval);
  }, [fetchBookings]);

  const loadHistory = async (bookingId: string) => {
    if (statusHistory[bookingId]) return;
    try {
      const res = await bookingsApi.getById(bookingId);
      setStatusHistory((prev) => ({ ...prev, [bookingId]: res.statusHistory }));
    } catch (err) {
      console.error('Failed to load booking detail:', err);
    }
  };

  const toggleExpand = (bookingId: string) => {
    if (expandedId === bookingId) {
      setExpandedId(null);
    } else {
      setExpandedId(bookingId);
      loadHistory(bookingId);
    }
  };

  const handleCancel = async (bookingId: string) => {
    setCancellingId(bookingId);
    try {
      await bookingsApi.cancel(bookingId, 'Cancelled by pet owner');
      toast.success(t('appointments_cancelled', 'Appointment cancelled'));
      fetchBookings();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || t('appointments_cancel_error', 'Failed to cancel'));
    } finally {
      setCancellingId(null);
    }
  };

  // Filter bookings
  const now = new Date();
  const filteredBookings = bookingsList.filter((b) => {
    const bookingDate = new Date(`${b.scheduledDate}T${b.scheduledTime}`);
    switch (activeFilter) {
      case 'upcoming':
        return !['completed', 'cancelled', 'no_show'].includes(b.status) && bookingDate >= now;
      case 'active':
        return ['assigned', 'confirmed', 'in_progress'].includes(b.status);
      case 'past':
        return ['completed', 'cancelled', 'no_show'].includes(b.status) || bookingDate < now;
      default:
        return true;
    }
  });

  const filterTabs: { key: FilterTab; label: string; count: number }[] = [
    {
      key: 'upcoming',
      label: t('appointments_upcoming', 'Upcoming'),
      count: bookingsList.filter((b) => !['completed', 'cancelled', 'no_show'].includes(b.status) && new Date(`${b.scheduledDate}T${b.scheduledTime}`) >= now).length,
    },
    {
      key: 'active',
      label: t('appointments_active', 'Active'),
      count: bookingsList.filter((b) => ['assigned', 'confirmed', 'in_progress'].includes(b.status)).length,
    },
    {
      key: 'past',
      label: t('appointments_past', 'Past'),
      count: bookingsList.filter((b) => ['completed', 'cancelled', 'no_show'].includes(b.status)).length,
    },
    {
      key: 'all',
      label: t('appointments_all', 'All'),
      count: bookingsList.length,
    },
  ];

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('appointments_title', 'My Appointments')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('appointments_subtitle', 'Track your pet care bookings and their status')}
          </p>
        </div>
        <button
          onClick={() => fetchBookings()}
          className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
          title={t('appointments_refresh', 'Refresh')}
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {filterTabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => { setActiveFilter(tab.key); setPage(1); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              activeFilter === tab.key
                ? 'bg-[#2D1F7B] text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                activeFilter === tab.key
                  ? 'bg-white/20 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Bookings List */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-[#2D1F7B]" />
        </div>
      ) : filteredBookings.length === 0 ? (
        <div className="text-center py-16">
          <Calendar className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <p className="text-gray-500 dark:text-gray-400 font-medium">
            {t('appointments_empty', 'No appointments found')}
          </p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
            {t('appointments_empty_hint', 'Book an appointment with a pet care provider to get started')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredBookings.map((booking) => {
            const config = STATUS_CONFIG[booking.status];
            const isExpanded = expandedId === booking.id;
            const bookingDate = new Date(`${booking.scheduledDate}T${booking.scheduledTime}`);
            const isPast = bookingDate < now;
            const canCancel = !['in_progress', 'completed', 'cancelled', 'no_show'].includes(booking.status);

            return (
              <div
                key={booking.id}
                className="bg-white dark:bg-[#1A1929] border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden"
              >
                {/* Main card */}
                <div
                  className="p-4 cursor-pointer hover:bg-gray-50/50 dark:hover:bg-white/[0.02] transition-colors"
                  onClick={() => toggleExpand(booking.id)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      {/* Service & Pet */}
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                          {booking.serviceName || booking.serviceType || t('appointments_appointment', 'Appointment')}
                        </h3>
                        {booking.isEmergency && (
                          <span className="text-xs px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full font-medium">
                            {t('appointments_emergency', 'Emergency')}
                          </span>
                        )}
                      </div>

                      {/* Pet name & Business */}
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {booking.petName}
                        {booking.businessName && (
                          <> &middot; {booking.businessName}</>
                        )}
                      </p>

                      {/* Date & Time */}
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {new Date(booking.scheduledDate).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {booking.scheduledTime}
                        </span>
                        {booking.duration && (
                          <span className="text-xs text-gray-400">
                            ({booking.duration} min)
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Status Badge + Expand */}
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${config.color} ${config.bgColor}`}>
                        <span>{config.icon}</span>
                        {t(`booking_status_${booking.status}`, config.label)}
                      </span>
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Progress Track (for active bookings) */}
                  {!['completed', 'cancelled', 'no_show'].includes(booking.status) && (
                    <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                      <div className="flex items-center gap-1">
                        {ACTIVE_FLOW_STEPS.map((step, idx) => {
                          const currentIdx = getStepIndex(booking.status);
                          const isComplete = currentIdx >= 0 && idx <= currentIdx;
                          const isCurrent = currentIdx === idx;
                          return (
                            <div key={step} className="flex items-center flex-1">
                              <div className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold shrink-0 ${
                                isComplete
                                  ? isCurrent
                                    ? 'bg-[#2D1F7B] text-white ring-2 ring-[#2D1F7B]/30'
                                    : 'bg-green-500 text-white'
                                  : 'bg-gray-200 dark:bg-gray-700 text-gray-400'
                              }`}>
                                {isComplete && !isCurrent ? (
                                  <CheckCircle2 className="w-3.5 h-3.5" />
                                ) : (
                                  idx + 1
                                )}
                              </div>
                              {idx < ACTIVE_FLOW_STEPS.length - 1 && (
                                <div className={`flex-1 h-0.5 mx-1 rounded ${
                                  currentIdx >= 0 && idx < currentIdx
                                    ? 'bg-green-500'
                                    : 'bg-gray-200 dark:bg-gray-700'
                                }`} />
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex justify-between mt-1 px-0.5">
                        {ACTIVE_FLOW_STEPS.map((step) => (
                          <span key={step} className="text-[10px] text-gray-400 dark:text-gray-500">
                            {t(`booking_step_${step}`, STATUS_CONFIG[step].label)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Expanded Detail */}
                {isExpanded && (
                  <div className="border-t border-gray-100 dark:border-gray-800 p-4 space-y-4 bg-gray-50/50 dark:bg-black/10">
                    {/* Business Contact */}
                    {(booking.businessPhone || booking.businessEmail) && (
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('appointments_provider', 'Provider')}
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {booking.businessName}
                        </p>
                        {booking.businessPhone && (
                          <p className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                            <Phone className="w-3.5 h-3.5" />
                            {booking.businessPhone}
                          </p>
                        )}
                        {booking.businessEmail && (
                          <p className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                            <Mail className="w-3.5 h-3.5" />
                            {booking.businessEmail}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Notes */}
                    {booking.notes && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                          {t('appointments_notes', 'Notes')}
                        </p>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{booking.notes}</p>
                      </div>
                    )}

                    {/* Completion Notes */}
                    {booking.completionNotes && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                          {t('appointments_completion_notes', 'Service Notes')}
                        </p>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{booking.completionNotes}</p>
                      </div>
                    )}

                    {/* Cancellation Reason */}
                    {booking.cancellationReason && (
                      <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/10 rounded-lg">
                        <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-xs font-medium text-red-600 dark:text-red-400">
                            {t('appointments_cancellation_reason', 'Cancellation Reason')}
                          </p>
                          <p className="text-sm text-red-700 dark:text-red-300">{booking.cancellationReason}</p>
                        </div>
                      </div>
                    )}

                    {/* Price Info */}
                    {(booking.estimatedPrice || booking.finalPrice) && (
                      <div className="flex gap-4">
                        {booking.estimatedPrice && (
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {t('appointments_estimated', 'Estimated')}
                            </p>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              ${booking.estimatedPrice.toFixed(2)}
                            </p>
                          </div>
                        )}
                        {booking.finalPrice && (
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {t('appointments_final_price', 'Final Price')}
                            </p>
                            <p className="text-sm font-semibold text-green-600 dark:text-green-400">
                              ${booking.finalPrice.toFixed(2)}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Status History Timeline */}
                    {statusHistory[booking.id] && statusHistory[booking.id].length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                          {t('appointments_timeline', 'Status Timeline')}
                        </p>
                        <div className="space-y-2">
                          {statusHistory[booking.id].map((entry, idx) => {
                            const toConfig = STATUS_CONFIG[entry.toStatus as BookingStatus] ?? {
                              label: entry.toStatus,
                              color: 'text-gray-500',
                              bgColor: 'bg-gray-100',
                              icon: '📋',
                            };
                            return (
                              <div key={entry.id} className="flex items-start gap-3">
                                <div className="flex flex-col items-center">
                                  <div className={`w-3 h-3 rounded-full ${
                                    idx === 0 ? 'bg-[#2D1F7B]' : 'bg-gray-300 dark:bg-gray-600'
                                  }`} />
                                  {idx < statusHistory[booking.id].length - 1 && (
                                    <div className="w-px h-6 bg-gray-200 dark:bg-gray-700" />
                                  )}
                                </div>
                                <div className="flex-1 -mt-0.5">
                                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                                    {toConfig.icon} {t(`booking_status_${entry.toStatus}`, toConfig.label)}
                                  </p>
                                  <p className="text-xs text-gray-400 dark:text-gray-500">
                                    {new Date(entry.createdAt).toLocaleString()}
                                  </p>
                                  {entry.reason && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 italic">
                                      {entry.reason}
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    {canCancel && (
                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCancel(booking.id);
                          }}
                          disabled={cancellingId === booking.id}
                          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/40 disabled:opacity-50 transition-colors"
                        >
                          {cancellingId === booking.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <XCircle className="w-4 h-4" />
                          )}
                          {t('appointments_cancel', 'Cancel Appointment')}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 pt-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 text-sm rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 disabled:opacity-50 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            {t('appointments_prev', 'Previous')}
          </button>
          <span className="px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1.5 text-sm rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 disabled:opacity-50 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            {t('appointments_next', 'Next')}
          </button>
        </div>
      )}
    </div>
  );
}
